/* -------------------------- Design imports start -------------------------- */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography } from '@mui/material'
import React, { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import CategoryDrawer from './CategoryDrawer'
import PropertyDrawer from './PropertyDrawer'
import GetConstant from './utils/constants'
// Removed old callback-based functions - now using React Query hooks
import FileInput from '../../components/inputs/FileInput'
import InputFileList, {
  InputFileListTableColumnIndices,
} from '../../components/widgets/InputFileList'
import { Form, type FormFieldType, type FormObject } from '../Form'
import SupplierDrawer from '../Supplier/SupplierDrawer'
import Button from '../../components/Button'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import LogTool from '../../logger/logTools'
import { type Category, type Property, type Supplier } from '../Supplier/utils/types'
import { type Material } from './utils/types'
import { type FileInfo, type FileUploadStatus, type SelectOption } from '../../utils/types'
import VersionDrawer from '../Version/components/VersionDrawer'
import VersionSubmit from '../Version/components/VersionSubmit'
import { type Version } from '../Version/utils/types'
import { type ArticleFormInput } from './utils/types'
// React Query imports
import { useSuppliers } from '../Supplier/hooks/useSupplierQueries'
import {
  useBrands,
  useAllCategories,
  useAllProperties,
  useAllMaterials,
} from './hooks/useArticleQueries'
import { useCreateCategoryOrProperty } from './hooks/useArticleMutations'
import {
  deleteFileFromUrl,
  fetchFileList as fetchFileListApi,
} from '../Filesystem/api/filesystemApi'
import { useUserContext } from '../../utils/context'
import { type InputFileListFileActions } from '../../components/widgets/InputFileList'
import useFetchExistingFiles from '../../hooks/useFetchExistingFiles'
import { FilesDrawer } from '../Filesystem'
/* ------------------------- Functional imports end ------------------------- */

interface Props {
  disabled?: boolean
  formState: {
    state: ArticleFormInput | undefined
    setState: React.Dispatch<React.SetStateAction<ArticleFormInput | undefined>>
  }
  inputFiles: {
    state: any[]
    // we have to support both types ArticleFile and File as we have to deal with
    // File objects before uploading the files, but with ArticleFile objects after
    // fetching the files
    setState: React.Dispatch<React.SetStateAction<any | undefined>>
  }
  uploadedFiles?: {
    state: FileInfo[]
    setState: React.Dispatch<React.SetStateAction<FileInfo[]>>
  }
  // use props for internal components to cusomize their behavior
  internalComponentProps?: {
    inputFileList?: Partial<
      InputFileListFileActions & { columns?: InputFileListTableColumnIndices[] }
    >
  }
  onReUploadFile?: (file: FileInfo, url: string) => Promise<void>
  enableVersioning?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */

export default function ArticleForm(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    disabled = false,
    formState,
    inputFiles,
    uploadedFiles,
    onReUploadFile = () => null,
    enableVersioning = false,
    internalComponentProps,
  } = props
  const log = new LogTool({ context: 'ArticleForm', enable: true, logLevel: 'debug' })
  const { t } = useTranslation(['item'])
  const { user } = useUserContext()
  const isGuestUser = user?.isGuestUser ?? false
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [openSupplierModal, setOpenSupplierModal] = useState(false)
  const [openVersionDrawer, setOpenVersionDrawer] = useState(false)
  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false)
  const [openPropertyDrawer, setOpenPropertyDrawer] = useState(false)
  const [openFilesDrawer, setOpenFilesDrawer] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  const supplierStore = useRef<Supplier[]>([] as Supplier[])
  const brandStore = useRef<string[]>([] as string[])
  const propertiesStore = useRef<Property[]>([] as Property[])
  const categoriesStore = useRef<Category[]>([] as Category[])
  const materialsStore = useRef<Material[]>([] as Material[])
  const [revertVersion, setRevertVersion] = useState<Version['data'] | undefined>(undefined)
  const [articleOptions, setArticleOptions] = useState<SelectOption[]>([])
  const [propertiesOptions, setPropertiesOptions] = useState<SelectOption[]>([])
  const [brandOptions, setBrandOptions] = useState<SelectOption[]>([])
  const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([])
  const [supplierFormInput, setSupplierFormInput] = useState<any>({} as Supplier)
  const [categoryOptions, setCategoryOptions] = useState<SelectOption[]>([])
  const [categoryFormInput, setCategoryFormInput] = useState<any>({} as Category)
  const [propertyFormInput, setPropertyFormInput] = useState<any>({} as Property)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ React Query Hooks ----------------------------- */
  // Disable these queries for guest users - they don't have access to these resources
  // Fetch suppliers using React Query
  const { data: suppliersData } = useSuppliers({}, !isGuestUser)
  // Fetch brands, categories, properties, and materials using React Query
  const { data: brandsData } = useBrands(!isGuestUser)
  const { data: categoriesData } = useAllCategories({}, !isGuestUser)
  const { data: propertiesData } = useAllProperties({}, !isGuestUser)
  const { data: materialsData } = useAllMaterials({}, !isGuestUser)
  // Mutation hook for creating categories, properties, or suppliers
  const createCategoryOrProperty = useCreateCategoryOrProperty()

  /* ------------------------------ Effects start ----------------------------- */
  // Update supplier store when data changes
  useEffect(() => {
    if (suppliersData?.results) {
      supplierStore.current = suppliersData.results

      const supplierOptions: SelectOption[] = supplierStore.current
        .map((supplier: Supplier): SelectOption | undefined => {
          if (!supplier || !supplier?.name || !supplier?.name) return undefined
          return { value: supplier.self as string, label: supplier.name }
        })
        .filter((s): s is SelectOption => s !== undefined)
      setSupplierOptions(supplierOptions)
    }
  }, [suppliersData])

  // Update brand store when data changes
  useEffect(() => {
    if (brandsData) {
      brandStore.current = brandsData

      setBrandOptions(
        Array.isArray(brandStore?.current)
          ? brandStore.current.map((brand: any) => ({
              value: brand?.self,
              label: brand?.name,
            }))
          : []
      )
    }
  }, [brandsData])

  // Update categories store when data changes
  useEffect(() => {
    if (categoriesData) {
      categoriesStore.current = categoriesData

      setCategoryOptions(
        categoriesStore.current.map((category: Category) => {
          return { value: category.self, label: category.name }
        })
      )
    }
  }, [categoriesData])

  // Update properties store when data changes
  useEffect(() => {
    if (propertiesData) {
      propertiesStore.current = propertiesData

      setPropertiesOptions(
        propertiesStore.current.map((property: Property) => {
          return { value: property.self, label: property.name }
        })
      )
    }
  }, [propertiesData])

  // Update materials store when data changes
  useEffect(() => {
    if (materialsData) {
      materialsStore.current = materialsData
    }
  }, [materialsData])
  /* ------------------------------ Effects start ----------------------------- */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Check if either a value or unit is specified for a dimension FormField. If so, the according unit / value is required!
   * @param dimension Key of the dimension FormField.
   * @returns
   */
  const isRequiredDimension = (dimension: string): boolean => {
    if (!formState.state) return false
    const dim = dimension as keyof ArticleFormInput
    const dimensionValue = formState.state[dim] as { value?: number; unit?: string } | undefined
    return (
      dimension in formState.state &&
      dimensionValue !== undefined &&
      ((!!dimensionValue?.value && !dimensionValue?.unit) ||
        (!dimensionValue?.value && !!dimensionValue?.unit))
    )
  }

  /**
   * Setter for the uploadStatus property on a FileInfo object within this component.
   * Do not use this function if you want to update multiple files at once as the component is rerendered after every status update!
   * @param file the file on which the uploadStatus should be updated.
   * @param status the updated uploadStatus.
   */
  function setFileUploadStatus(file: FileInfo, status: FileUploadStatus): void {
    inputFiles.setState((prev: FileInfo[]) =>
      prev.map((listedFile: FileInfo) => {
        if (listedFile.name === file.name) {
          // add an upload-status of success to the file info object or overwrite it with the provided status
          return Object.assign(file, { uploadStatus: status })
        }
        return listedFile
      })
    )
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleRevertVersion = (version: Version): void => {
    log.debug('handleRevertVersion', version)
    setRevertVersion(version.data)

    // Iterate over all keys in version.data
    Object.keys(version.data).forEach((key: string) => {
      // Check if the key exists in formState.state and if the values are different from each other
      const formStateValue: unknown = formState.state
        ? formState.state[key as keyof ArticleFormInput]
        : undefined
      const versionDataValue: unknown = version.data[key]
      if (formState.state?.hasOwnProperty(key) && formStateValue !== versionDataValue) {
        if (key === 'category' || key === 'properties') {
          formState.setState((prevState: any) => ({
            ...prevState,
          }))
        } else if (
          key === 'length' ||
          key === 'width' ||
          key === 'height' ||
          key === 'weight' ||
          key === 'volume'
        ) {
          const dimension = versionDataValue as
            | { value?: number | string; unit?: string }
            | undefined
          if (!dimension) {
            return
          }

          formState.setState((prevState: any) => ({
            ...prevState,
            [key]: {
              ...(prevState?.[key as keyof ArticleFormInput] as
                | Record<string, unknown>
                | undefined),
              value: dimension.value,
              unit: dimension.unit,
            },
          }))
        } else {
          // if values differ, overwrite the value in formState.state with the value from version.data
          formState.setState((prevState: any) => ({
            ...prevState,
            [key]: version.data[key],
          }))
        }
      }
    })
  }

  const handleAddArticleOption = (
    input: any,
    url: 'v1/supplier/suppliers/' | 'v1/items/categories/' | 'v1/items/properties/'
  ): void => {
    createCategoryOrProperty.mutate(
      { url, input },
      {
        onSuccess: (response: Supplier | Category | Property) => {
          // update supplier select options
          switch (url) {
            case 'v1/supplier/suppliers/':
              setSupplierOptions((prev: SelectOption[]) => {
                return [...prev, { value: response.self, label: response.name } as SelectOption]
              })
              formState.setState((prev: any) => {
                return { ...prev, supplier: { value: response.self, label: response.name } }
              })
              toast.success(t('item:feedback.success.creatingSupplier'))
              break
            case 'v1/items/categories/':
              log.debug('response', response)
              setCategoryOptions((prev: SelectOption[]) => {
                return [...prev, { value: response.self, label: response.name } as SelectOption]
              })
              formState.setState((prev: any) => {
                return { ...prev, category: [{ value: response?.self, label: response?.name }] }
              })
              toast.success(t('item:feedback.success.creatingCategory'))
              break
            case 'v1/items/properties/':
              setPropertiesOptions((prev: SelectOption[]) => {
                return [...prev, { value: response.self, label: response.name } as SelectOption]
              })
              formState.setState((prev: any) => {
                return { ...prev, properties: { value: response.self, label: response.name } }
              })
              toast.success(t('item:feedback.success.creatingProperty'))
              break
            default:
              break
          }
        },
        onError: (error: any) => {
          switch (url) {
            case 'v1/supplier/suppliers/':
              toast.error(t('item:feedback.error.creatingSupplier'))
              break
            case 'v1/items/categories/':
              toast.error(t('item:feedback.error.creatingCategory'))
              break
            case 'v1/items/properties/':
              toast.error(t('item:feedback.error.creatingProperty'))
              break
            default:
              break
          }
          log.error('Error creating option', error)
        },
      }
    )
  }

  const handleReUploadFile = async (file: FileInfo, url: string): Promise<void> => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // retry to upload the file
    await onReUploadFile(file, url)
  }

  const handleRemoveFileFromList = (file: FileInfo): void => {
    // update rendered files list
    inputFiles.setState((prevFileList: FileInfo[]) => {
      // get the location of the file to remove within the files list
      const fileIndex = prevFileList.findIndex(listedFile => listedFile.name === file.name)
      // remove the file from the list. Note that this does not work on the prevFileList directly
      const updatedFileList = [...prevFileList]
      updatedFileList.splice(fileIndex, 1)

      // return the files list where the file is removed
      return updatedFileList
    })
  }

  const handleDeleteFile = async (file: FileInfo): Promise<void> => {
    // set the uploadStatus of the current file to loading
    setFileUploadStatus(file, 'loading')
    // try to delete the file
    try {
      // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
      const deleteUrl = formState.state?.self + 'files/' + file.self!.split('/').slice(-2)[0] + '/'
      await deleteFileFromUrl(deleteUrl)
      handleRemoveFileFromList(file)
    } catch (error) {
      log.error('Error deleting article file', error)
      setFileUploadStatus(file, 'deleteError')
    }
  }
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const generalFormFields = GetConstant({
    name: 'generalFormFields',
    brandOptions: brandOptions,
    articleOptions: articleOptions,
    categoryOptions: categoryOptions,
    propertyOptions: propertiesOptions,
    onAddPropertyOption: () => {
      setOpenPropertyDrawer(true)
    },
    onAddCategoryOption: () => {
      setOpenCategoryDrawer(true)
    },
  }) as FormFieldType[]
  const dimensionsFormFields = GetConstant({
    name: 'dimensionsFormFields',
    isRequiredDimension,
  }) as FormFieldType[]

  // Separate fields for accordions
  // Always visible: name, number, description, material
  const alwaysVisibleFields = generalFormFields.filter(field =>
    ['name', 'number', 'description', 'material'].includes(String(field.key))
  )

  // General Information accordion: type, family, brand, categories, properties, variationOf, qualityClass, barcodeText, barcodeType
  const generalInfoFields = generalFormFields.filter(field =>
    [
      'type',
      'family',
      'brand',
      'category',
      'properties',
      'variationOf',
      'qualityClass',
      'barcodeText',
      'barcodeType',
    ].includes(String(field.key))
  )
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  // initialize select options every time the modal opens

  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('formState ->', formState.state, ', revertVersion ->', revertVersion)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <>
      <Grid container width="100%" height="100%">
        {/* Always visible fields: Name, Number, Description, Material */}
        <Form
          editing={!disabled}
          formFields={alwaysVisibleFields}
          formObject={formState.state ?? {}}
          onChange={(value: FormObject) => formState.setState(value as ArticleFormInput)}
        />

        {/* File Upload Section */}
        <Grid item xs={12} sx={{ marginTop: '20px' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '1rem',
            }}
          >
            <Typography variant="h6">{t('files:content.label.uploadFiles')}</Typography>

            {/* Already Uploaded Files - Button to open FilesDrawer */}
            {uploadedFiles && uploadedFiles.state.length > 0 && (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenFilesDrawer(true)}
                sx={{
                  textTransform: 'none',
                  color: 'white',
                  display: 'flex',
                }}
              >
                {t('common:content.label.viewUploadedFiles', {
                  count: uploadedFiles.state.length,
                }) || `${t('common:content.label.uploadedFiles')} (${uploadedFiles.state.length})`}
              </Button>
            )}
          </Box>
          <FileInput
            disabled={disabled}
            inputState={{ state: inputFiles.state, setState: inputFiles.setState }}
            maxAllowedFiles={10}
          />
          <Typography variant="body1">
            {t('files:content.description.articleFilesUploadHint')}
          </Typography>
          {inputFiles.state?.length > 0 && (
            <InputFileList
              disabled={disabled}
              files={inputFiles.state}
              noFilesInfo={t('item:feedback.info.noArticleFiles')}
              enableDownloadFile={
                typeof internalComponentProps?.inputFileList?.enableDownloadFile !== 'undefined'
                  ? internalComponentProps.inputFileList.enableDownloadFile
                  : true
              }
              onRemoveFileFromList={
                internalComponentProps?.inputFileList?.onRemoveFileFromList ??
                handleRemoveFileFromList
              }
              onReUploadFile={
                internalComponentProps?.inputFileList?.onReUploadFile ?? handleReUploadFile
              }
              onDeleteFile={internalComponentProps?.inputFileList?.onDeleteFile ?? handleDeleteFile}
              onViewFile={internalComponentProps?.inputFileList?.onViewFile}
              onEditFile={internalComponentProps?.inputFileList?.onEditFile}
              columns={internalComponentProps?.inputFileList?.columns}
              sx={{
                marginTop: '2rem',
                width: '100%',
              }}
            />
          )}
          {enableVersioning && <VersionSubmit formState={formState} />}
        </Grid>

        {/* General Information Accordion */}
        <Grid item xs={12}>
          <Accordion style={{ marginTop: '20px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="generalInfoAccordion">
              <Typography variant="subtitle1">
                {t('common:content.label.generalInformation')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Form
                editing={!disabled}
                formFields={generalInfoFields}
                formObject={formState.state ?? {}}
                onChange={(value: FormObject) => formState.setState(value as ArticleFormInput)}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Dimensions Accordion */}
        <Grid item xs={12} mb="16px">
          <Accordion style={{ marginTop: '20px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="dimensionsAccordion">
              <Typography variant="subtitle1">
                {t('common:content.label.dimensionWeight')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="subtitle2" sx={{ mb: '30px' }}>
                {t('common:feedback.info.dimensionWeight')}
              </Typography>
              <Form
                editing={!disabled}
                formFields={dimensionsFormFields}
                formObject={formState.state ?? {}}
                onChange={(value: FormObject) => formState.setState(value as ArticleFormInput)}
              />
            </AccordionDetails>
          </Accordion>
        </Grid>

        {/* Version History Accordion */}
        {enableVersioning && (
          <Accordion defaultExpanded style={{ marginTop: '20px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} id="versionHistoryAccordion">
              <Typography variant="subtitle1">
                {t('common:content.label.versionHistory')}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <VersionSubmit formState={formState} />
            </AccordionDetails>
          </Accordion>
        )}
      </Grid>

      <SupplierDrawer
        open={openSupplierModal}
        setOpen={setOpenSupplierModal}
        onCreateConfirm={() => handleAddArticleOption(supplierFormInput, 'v1/supplier/suppliers/')}
        formState={{ state: supplierFormInput, setState: setSupplierFormInput }}
      />
      <CategoryDrawer
        open={openCategoryDrawer}
        setOpen={setOpenCategoryDrawer}
        onClose={() => null}
        formState={{ state: categoryFormInput, setState: setCategoryFormInput }}
        onCreateConfirm={() => handleAddArticleOption(categoryFormInput, 'v1/items/categories/')}
        enableEditMode={false}
      />
      <PropertyDrawer
        open={openPropertyDrawer}
        setOpen={setOpenPropertyDrawer}
        onClose={() => null}
        formState={{ state: propertyFormInput, setState: setPropertyFormInput }}
        onCreateConfirm={() => handleAddArticleOption(propertyFormInput, 'v1/items/properties/')}
        enableEditMode={false}
      />
      <VersionDrawer
        open={openVersionDrawer}
        setOpen={setOpenVersionDrawer}
        formState={formState}
        onConfirm={() => null}
        onRevertVersion={handleRevertVersion}
      />
      <FilesDrawer
        open={openFilesDrawer}
        setOpen={setOpenFilesDrawer}
        files={useMemo(() => {
          // Use already fetched files if available, otherwise use the URL
          if (uploadedFiles && uploadedFiles.state.length > 0) {
            return uploadedFiles.state
          }
          // Always construct the article-specific files URL directly from formState.state.self
          // This ensures we always use the correct article endpoint, not a general files endpoint
          const filesUrl = formState.state?.self ? `${formState.state.self}files/` : undefined
          return filesUrl
        }, [formState.state?.self, uploadedFiles])}
        title={t('common:content.label.uploadedFiles')}
        size="big"
        refreshKey={uploadedFiles && uploadedFiles.state.length}
      />
    </>
  )
}
